import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { Box, Typography, Button, CircularProgress, Paper, Stack } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";

const ClaimLead = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const staffId = searchParams.get("staffId");

  const [status, setStatus] = useState("loading"); // loading | pending | already_claimed | expired | not_found | success | error | missing_staff
  const [lead, setLead] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchClaimDetails = async () => {
    if (!staffId) {
      setStatus("missing_staff");
      return;
    }

    try {
      setStatus("loading");

      const res = await axiosInstance.get(`api/webhooks/claim/${token}`);

      const data = res.data?.data;

      if (data?.status === "claimed") {
        setStatus("already_claimed");
        return;
      }

      setLead(data?.lead || null);
      setStatus("pending");
    } catch (err) {
      const code = err.response?.status;

      if (code === 404) {
        setStatus("not_found");
      } else if (code === 410) {
        setStatus("expired");
      } else {
        setStatus("error");
        setErrorMessage(err.response?.data?.message || "Something went wrong loading this lead.");
      }
    }
  };

  useEffect(() => {
    console.log({ token, staffId });
    fetchClaimDetails(); // eslint-disable-next-line
  }, [token, staffId]);

  const handleClaim = async () => {
    try {
      setClaiming(true);
      setErrorMessage("");

      await axiosInstance.post(`api/webhooks/claim/${token}/claim`, { staffId });

      setStatus("success");
    } catch (err) {
      const code = err.response?.status;

      if (code === 409) {
        setStatus("already_claimed");
      } else if (code === 410) {
        setStatus("expired");
      } else {
        setErrorMessage(err.response?.data?.message || "Couldn't claim this lead. Try again.");
      }
    } finally {
      setClaiming(false);
    }
  };

  const renderContent = () => {
    if (status === "missing_staff") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#ef4444" }} />
          <Typography variant="h6">Invalid claim link</Typography>
          <Typography color="text.secondary" align="center">
            This link is missing required information. Please use the link from your email.
          </Typography>
        </Stack>
      );
    }

    if (status === "loading") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <CircularProgress size={32} />
          <Typography color="text.secondary">Loading lead details...</Typography>
        </Stack>
      );
    }

    if (status === "not_found") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#9ca3af" }} />
          <Typography variant="h6">Claim link not found</Typography>
          <Typography color="text.secondary" align="center">
            This link may be invalid. Please check the email and try again.
          </Typography>
        </Stack>
      );
    }

    if (status === "expired") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#f59e0b" }} />
          <Typography variant="h6">This link has expired</Typography>
          <Typography color="text.secondary" align="center">
            Please contact your admin if you still need access to this lead.
          </Typography>
        </Stack>
      );
    }

    if (status === "already_claimed") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <BlockIcon sx={{ fontSize: 48, color: "#ef4444" }} />
          <Typography variant="h6">Lead already claimed</Typography>
          <Typography color="text.secondary" align="center">
            Another team member has already claimed this lead.
          </Typography>
        </Stack>
      );
    }

    if (status === "success") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: "#22c55e" }} />
          <Typography variant="h6">Lead claimed successfully</Typography>
          <Typography color="text.secondary" align="center">
            This lead is now assigned to you.
          </Typography>
        </Stack>
      );
    }

    if (status === "error") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#ef4444" }} />
          <Typography variant="h6">Something went wrong</Typography>
          <Typography color="text.secondary" align="center">
            {errorMessage}
          </Typography>
        </Stack>
      );
    }

    // status === "pending"
    return (
      <Stack spacing={2}>
        <Typography variant="h6">New Lead Available</Typography>

        <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Address
          </Typography>
          <Typography sx={{ mb: 1, fontWeight: 500 }}>{lead?.full_address || lead?.address || "-"}</Typography>

          <Typography variant="body2" color="text.secondary">
            City / State / ZIP
          </Typography>
          <Typography sx={{ fontWeight: 500 }}>{[lead?.city, lead?.state, lead?.zip].filter(Boolean).join(", ") || "-"}</Typography>
        </Box>

        {errorMessage && (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        )}

        <Button variant="contained" size="large" onClick={handleClaim} disabled={claiming} fullWidth>
          {claiming ? "Claiming..." : "Claim This Lead"}
        </Button>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f3f4f6",
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, width: "100%", maxWidth: 420, borderRadius: 3 }}>{renderContent()}</Paper>
    </Box>
  );
};

export default ClaimLead;
